import axios from 'axios'
import { getApiHost } from '../conf'

export interface DocumentInfo {
  id: string
  name: string
  url?: string
  type: 'accessibility-action-plan' | 'accessibility-multiannual-scheme' | 'web-accessibility-declaration'
  language: 'fr' | 'en'
}

export interface DocumentDownloadResponse {
  url: string
  fileName: string
}

// Mapping of document types to actual file names in Azure Storage
const DOCUMENT_FILE_MAPPING = {
  'accessibility-action-plan': {
    'fr': '0825 Plan d\'action annuel Accessibilite 2025_fr.pdf',
    'en': '0825 Action plan accessibility_en.pdf'
  },
  'accessibility-multiannual-scheme': {
    'fr': '0825 Schema pluriannuel accessibilite numerique 2025-2027_fr.pdf',
    'en': '0825 Multi-year accessibility plan_en.pdf'
  },
  'web-accessibility-declaration': {
    'fr': 'Declaration-RAWeb-Startupconnexion_fr.pdf',
    'en': 'Declaration-RAWeb-Startupconnexion_en.pdf'
  }
}

/**
 * Service to handle document operations with Azure Storage
 */
class DocumentService {
  
  /**
   * Get document as blob from backend API endpoint
   * @param documentType - Type of document to retrieve
   * @param language - Language of the document
   * @returns Promise with the document blob URL
   */
  async getDocumentUrl(documentType: string, language: string = 'fr'): Promise<DocumentDownloadResponse> {
    // Get the actual file name from our mapping
    const fileName = DOCUMENT_FILE_MAPPING[documentType as keyof typeof DOCUMENT_FILE_MAPPING]?.[language as 'fr' | 'en']
    
    if (!fileName) {
      throw new Error(`Document not found: ${documentType} in ${language}`)
    }

    try {
      // Use axios directly for blob response
      const response = await axios({
        method: 'GET',
        url: `/documents/${encodeURIComponent(fileName)}`,
        params: { language },
        baseURL: getApiHost(),
        responseType: 'blob', // Important: specify blob response type
        withCredentials: true,
      })
      
      const blob = (response.data as unknown) as Blob
      console.log('Blob created:', blob.size, 'bytes, type:', blob.type)
      
      // Create a blob URL for the PDF
      const blobUrl = URL.createObjectURL(blob)
      console.log('Blob URL created:', blobUrl)
      
      return {
        url: blobUrl,
        fileName: fileName
      }
    } catch (error) {
      console.error('Error fetching document:', error)
      console.error('Request for document:', fileName)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to fetch document: ${documentType} - ${errorMessage}`)
    }
  }

  /**
   * Open document in a new tab (direct backend URL approach)
   * @param documentType - Type of document to open
   * @param language - Language of the document
   */
  openDocumentInNewTab(documentType: string, language: string = 'fr'): void {
    try {
      // Get the actual file name from our mapping
      const fileName = DOCUMENT_FILE_MAPPING[documentType as keyof typeof DOCUMENT_FILE_MAPPING]?.[language as 'fr' | 'en']
      
      if (!fileName) {
        throw new Error(`Document not found: ${documentType} in ${language}`)
      }

      // Create direct URL to backend endpoint
      const documentUrl = `${getApiHost()}/documents/${encodeURIComponent(fileName)}?language=${language}`
      
      console.log('Opening document URL directly:', documentUrl)
      
      // Open the backend URL directly in a new tab
      // This should work if your backend sets the correct Content-Disposition header
      window.open(documentUrl, '_blank', 'noopener,noreferrer')
      
    } catch (error) {
      console.error('Error opening document:', error)
      throw new Error(`Failed to open document: ${documentType}`)
    }
  }

  /**
   * Download document directly
   * @param documentType - Type of document to download
   * @param language - Language of the document
   */
  async downloadDocument(documentType: string, language: string = 'fr'): Promise<void> {
    try {
      const response = await this.getDocumentUrl(documentType, language)
      
      // Create a temporary link to trigger download
      const link = document.createElement('a')
      link.href = response.url
      link.download = response.fileName
      link.style.display = 'none'
      
      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up the blob URL after download
      setTimeout(() => {
        URL.revokeObjectURL(response.url)
      }, 100)
    } catch (error) {
      console.error('Error downloading document:', error)
      throw new Error(`Failed to download document: ${documentType}`)
    }
  }

  /**
   * Get all available documents
   * @param language - Language filter
   * @returns Promise with list of available documents
   */
  async getAvailableDocuments(language?: string): Promise<DocumentInfo[]> {
    // Since we have a fixed set of documents, return them directly
    const documentTypes = Object.keys(DOCUMENT_FILE_MAPPING) as (keyof typeof DOCUMENT_FILE_MAPPING)[]
    const availableLanguages = language ? [language as 'fr' | 'en'] : ['fr', 'en'] as const
    
    const documents: DocumentInfo[] = []
    
    documentTypes.forEach(type => {
      availableLanguages.forEach(lang => {
        const fileName = DOCUMENT_FILE_MAPPING[type][lang]
        if (fileName) {
          documents.push({
            id: `${type}-${lang}`,
            name: fileName,
            type: type,
            language: lang
          })
        }
      })
    })
    
    return Promise.resolve(documents)
  }

  /**
   * Test if backend is reachable
   */
  async testBackendConnection(): Promise<boolean> {
    try {
      console.log('Testing backend connection via axios...')
      
      await axios({
        method: 'GET',
        url: '/documents/test',
        baseURL: getApiHost(),
        withCredentials: true,
      })
      
      console.log('Backend connection test successful')
      return true
    } catch (error) {
      console.error('Backend connection test failed:', error)
      return false
    }
  }

  /**
   * Get all available document mappings (for debugging/testing)
   */
  getDocumentMappings() {
    return DOCUMENT_FILE_MAPPING
  }
}

// Export a singleton instance
export default new DocumentService()
